import { normalizeInsurerKey } from './insurerLogoFromOrgOptions'

export function linkTextValue(items) {
  const arr = Array.isArray(items) ? items : []
  return arr.map((x) => String(x?.text || '').trim()).filter(Boolean).join('\n')
}

export function linkItemsFromText(value) {
  return String(value || '')
    .split(/\n|,/)
    .map((x) => x.trim())
    .filter(Boolean)
    .map((text) => {
      const digits = text.replace(/\D/g, '')
      return digits.length >= 7 && /^[0-9\-\s()]+$/.test(text)
        ? { text, href: `tel:${digits}` }
        : { text }
    })
}

export function flattenInsurerSystemItems(sections) {
  return (Array.isArray(sections) ? sections : []).flatMap((section) => (
    Array.isArray(section.items)
      ? section.items.map((item) => ({
        ...item,
        sectionId: section.id,
        sectionTitle: section.title,
        firstColLabel: section.firstColLabel,
      }))
      : []
  ))
}

export function findInsurerSystemItem(row, systemItems) {
  const names = [row?.orgName, row?.displayName, row?.name].filter(Boolean)
  const keys = names.map((name) => normalizeInsurerKey(name)).filter(Boolean)
  const exact = (Array.isArray(systemItems) ? systemItems : [])
    .find((item) => keys.includes(normalizeInsurerKey(item?.name)))
  if (exact) return exact

  return (Array.isArray(systemItems) ? systemItems : []).find((item) => {
    const itemKey = normalizeInsurerKey(item?.name)
    if (!itemKey) return false
    return keys.some((key) => key.length >= 3 && (itemKey.includes(key) || key.includes(itemKey)))
  }) || null
}

export function mergeInsurerSystemSections(staticSections, profiles, needle, favorites) {
  const normalizedNeedle = String(needle || '').trim().toLowerCase().replace(/\s+/g, '')
  const profileByName = new Map()
  const unmatchedProfiles = []

  for (const profile of Array.isArray(profiles) ? profiles : []) {
    const key = normalizeInsurerKey(profile?.orgName || profile?.insurerName)
    if (key) profileByName.set(key, profile)
    unmatchedProfiles.push(profile)
  }

  const sections = (Array.isArray(staticSections) ? staticSections : []).map((sec) => {
    const mergedItems = (Array.isArray(sec.items) ? sec.items : []).map((it) => {
      const key = normalizeInsurerKey(it?.name)
      const profile = profileByName.get(key)
      if (!profile) return it
      profileByName.delete(key)
      return normalizeProfileItem(profile, it)
    })
    const filtered = mergedItems.filter((it) => {
      if (!normalizedNeedle) return true
      const hay = String(it?.name || '').toLowerCase().replace(/\s+/g, '')
      return hay.includes(normalizedNeedle)
    })

    const items = filtered.slice().sort((a, b) => {
      const af = favorites?.has?.(`${sec.id}::${String(a?.name || '')}`) ? 1 : 0
      const bf = favorites?.has?.(`${sec.id}::${String(b?.name || '')}`) ? 1 : 0
      if (af !== bf) return bf - af
      return String(a?.name || '').localeCompare(String(b?.name || ''), 'ko')
    })

    return { ...sec, items }
  })

  const knownSectionIds = new Set(sections.map((sec) => sec.id))
  const extraBySection = new Map()
  for (const profile of unmatchedProfiles) {
    const key = normalizeInsurerKey(profile?.orgName || profile?.insurerName)
    if (key && !profileByName.has(key)) continue
    const sectionId = profile?.sectionId || 'fire'
    const name = profile?.orgName || profile?.insurerName || profile?.insurerCode || '보험사'
    if (normalizedNeedle && !String(name).toLowerCase().replace(/\s+/g, '').includes(normalizedNeedle)) continue
    const list = extraBySection.get(sectionId) || []
    list.push(normalizeProfileItem(profile, { name }))
    extraBySection.set(sectionId, list)
  }

  for (const [sectionId, items] of extraBySection.entries()) {
    if (!items.length) continue
    const existing = sections.find((sec) => sec.id === sectionId)
    const sorted = items.slice().sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'ko'))
    if (existing) {
      existing.items = [...existing.items, ...sorted]
    } else if (!knownSectionIds.has(sectionId)) {
      sections.push({
        id: sectionId,
        title: items[0]?.sectionTitle || sectionId,
        firstColLabel: items[0]?.firstColLabel || '보험회사 전산',
        items: sorted,
      })
    }
  }

  return sections
}

function normalizeProfileItem(profile, fallback) {
  return {
    ...fallback,
    ...profile,
    name: profile.orgName || profile.insurerName || fallback.name,
    logoUrl: profile.logoUrl || fallback.logoUrl,
    systemUrl: profile.systemUrl || '',
    termsUrl: profile.termsUrl || '',
    formUrl: profile.formUrl || '',
  }
}
